import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AdminLayout } from './shared/layout/AdminLayout';
import { ColaboradorLayout } from './shared/layout/ColaboradorLayout';
import { NotFoundPage } from './shared/layout/NotFoundPage';
import { RequireAuth } from './shared/auth/RequireAuth';
import { RequireRole } from './shared/auth/RequireRole';
import { LoginPage } from './features/auth/LoginPage';
import { CargosListPage } from './features/cadastros-gerais/cargos/pages/CargosListPage';
import { FeriadosListPage } from './features/cadastros-gerais/feriados/pages/FeriadosListPage';
import { ImpostosListPage } from './features/cadastros-gerais/impostos/pages/ImpostosListPage';
import { TiposHoraListPage } from './features/cadastros-gerais/tipos-hora/pages/TiposHoraListPage';
import { TiposTarefaListPage } from './features/cadastros-gerais/tipos-tarefa/pages/TiposTarefaListPage';
import { ParametrosPage } from './features/cadastros-gerais/parametros/pages/ParametrosPage';
import { CompaniesListPage } from './features/empresa/pages/CompaniesListPage';
import { NotasFiscaisEmpresaPage } from './features/empresa/notas-fiscais/pages/NotasFiscaisEmpresaPage';
import { GerarNotaFiscalEmpresaPage } from './features/empresa/notas-fiscais-gerar/pages/GerarNotaFiscalEmpresaPage';
import { EmpresaExtratoFaturamentoPage } from './features/empresa/extrato-faturamento/pages/EmpresaExtratoFaturamentoPage';
import { ClientsListPage } from './features/clientes/pages/ClientsListPage';
import { ClientProjectsPage } from './features/clientes/projects/pages/ClientProjectsPage';
import { ProjetosGlobalPage } from './features/projetos/pages/ProjetosGlobalPage';
import { PurchaseRequestsListPage } from './features/requisicoes-compra/pages/PurchaseRequestsListPage';
import { PurchaseRequestDetailPage } from './features/requisicoes-compra/pages/PurchaseRequestDetailPage';
import { EmployeesListPage } from './features/colaboradores/pages/EmployeesListPage';
import { FaltasGlobalPage } from './features/colaboradores/faltas-global/FaltasGlobalPage';
import { FeriasGlobalPage } from './features/colaboradores/ferias-global/FeriasGlobalPage';
import { ValesGlobalPage } from './features/colaboradores/vales-global/ValesGlobalPage';
import { NotasFiscaisGlobalPage } from './features/colaboradores/notas-fiscais-global/NotasFiscaisGlobalPage';
import { TimesheetEntriesListPage } from './features/apontamentos/pages/TimesheetEntriesListPage';
import { MeusApontamentos } from './features/apontamentos/pages/MeusApontamentos';
import { MeusVales } from './features/colaboradores/vales/pages/MeusVales';
import { MeusNotasFiscais } from './features/colaboradores/notas-fiscais/pages/MeusNotasFiscais';
import { ReportPreview } from './features/dev/ReportPreview';
import { EspelhoPontoPage } from './features/relatorios/espelho-ponto/EspelhoPontoPage';
import { ExtratoFaturamentoPage } from './features/relatorios/extrato-faturamento/ExtratoFaturamentoPage';
import { NotasFiscaisColabPage } from './features/relatorios/notas-fiscais-colab/NotasFiscaisColabPage';
import { ValesPage as RelatorioValesPage } from './features/relatorios/vales/ValesPage';
import { AdminDashboardPage } from './features/dashboard/pages/AdminDashboardPage';
import { ColaboradorHomePage } from './features/dashboard/pages/ColaboradorHomePage';

// Placeholders — substituídos por features reais nas tasks T-10+.
function Placeholder({ title }: { title: string }): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <Typography variant="h4" component="h1" gutterBottom>
        {title}
      </Typography>
      <Typography color="text.secondary">{t('layout.placeholderBody')}</Typography>
    </>
  );
}

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <RequireRole allow="ADMINISTRATIVO">
          <AdminLayout />
        </RequireRole>
      </RequireAuth>
    ),
    children: [
      { index: true, element: <AdminDashboardPage /> },
      { path: 'empresas', element: <CompaniesListPage /> },
      { path: 'empresas/notas-fiscais', element: <NotasFiscaisEmpresaPage /> },
      { path: 'empresas/notas-fiscais/gerar', element: <GerarNotaFiscalEmpresaPage /> },
      { path: 'empresas/extrato', element: <EmpresaExtratoFaturamentoPage /> },
      { path: 'clientes', element: <ClientsListPage /> },
      { path: 'clientes/:clientId/projetos', element: <ClientProjectsPage /> },
      { path: 'clientes/projetos', element: <ProjetosGlobalPage /> },
      { path: 'clientes/requisicoes-compra', element: <PurchaseRequestsListPage /> },
      { path: 'clientes/requisicoes-compra/:id', element: <PurchaseRequestDetailPage /> },
      { path: 'colaboradores', element: <EmployeesListPage /> },
      { path: 'apontamentos', element: <TimesheetEntriesListPage /> },
      { path: 'colaboradores/apontamentos', element: <Navigate to="/apontamentos" replace /> },
      { path: 'colaboradores/faltas', element: <FaltasGlobalPage /> },
      { path: 'colaboradores/ferias', element: <FeriasGlobalPage /> },
      // Sem rota dedicada de Taxas: gestão acontece no sub-modal do
      // EmployeeFormDialog (sub-recurso /employees/{id}/taxas).
      { path: 'colaboradores/vales', element: <ValesGlobalPage /> },
      { path: 'colaboradores/notas-fiscais', element: <NotasFiscaisGlobalPage /> },
      { path: 'cadastros-gerais/cargos', element: <CargosListPage /> },
      { path: 'cadastros-gerais/feriados', element: <FeriadosListPage /> },
      { path: 'cadastros-gerais/impostos', element: <ImpostosListPage /> },
      { path: 'cadastros-gerais/tipos-hora', element: <TiposHoraListPage /> },
      { path: 'cadastros-gerais/tipos-tarefa', element: <TiposTarefaListPage /> },
      { path: 'parametros', element: <ParametrosPage /> },
      { path: 'relatorios', element: <Navigate to="/relatorios/espelho-ponto" replace /> },
      { path: 'relatorios/espelho-ponto', element: <EspelhoPontoPage /> },
      { path: 'relatorios/extrato-faturamento', element: <ExtratoFaturamentoPage /> },
      { path: 'relatorios/notas-fiscais-colab', element: <NotasFiscaisColabPage /> },
      { path: 'relatorios/vales', element: <RelatorioValesPage /> },
      { path: 'dev/report-preview', element: <ReportPreview /> },
    ],
  },
  {
    path: '/me',
    element: (
      <RequireAuth>
        <RequireRole allow="COLABORADOR">
          <ColaboradorLayout />
        </RequireRole>
      </RequireAuth>
    ),
    children: [
      { index: true, element: <ColaboradorHomePage /> },
      { path: 'apontamentos', element: <MeusApontamentos /> },
      { path: 'vales', element: <MeusVales /> },
      { path: 'notas-fiscais', element: <MeusNotasFiscais /> },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
]);
